import { useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient, keepPreviousData, QueryKey } from '@tanstack/react-query';
import { ScheduleItem, ScheduleItemsResponse, StatusTotals } from '@app-types';

interface UseSchedulePageDataOptions {
  queryKey: QueryKey;
  queryFn: () => Promise<ScheduleItemsResponse>;
  pageSize: number; // Required for correct totalPages calculation
  enabled?: boolean;
}

export interface UseSchedulePageDataReturn {
  // Data
  scheduleItems: ScheduleItem[];
  isLoading: boolean;
  error: Error | null;
  isFetching: boolean;

  // Response
  response: ScheduleItemsResponse | undefined;

  // Status totals
  filterCounts: Record<string, number>;

  // Pagination
  totalItems: number;
  totalPages: number;

  // Actions
  refreshData: () => void;
}

/**
 * Generic hook for data fetching in schedule pages (Scheduler & Assessor)
 * Handles: data fetching, statusTotals preservation, filter counts, pagination
 */
export function useSchedulePageData(options: UseSchedulePageDataOptions): UseSchedulePageDataReturn {
  const { queryKey, queryFn, pageSize, enabled = true } = options;
  const queryClient = useQueryClient();

  // Fetch data with placeholderData to prevent UI flicker
  const {
    data: response,
    isLoading,
    error,
    isFetching
  } = useQuery<ScheduleItemsResponse>({
    queryKey,
    queryFn,
    staleTime: 0,
    refetchOnMount: 'always',
    placeholderData: keepPreviousData,
    enabled
  });

  // Extract schedule items
  const scheduleItems = useMemo(() => response?.data || [], [response]);

  // Preserve last known totals to avoid counter flicker between filter changes
  const [lastStatusTotals, setLastStatusTotals] = useState<StatusTotals | null>(null);
  useEffect(() => {
    if (response?.statusTotals) {
      setLastStatusTotals(response.statusTotals);
    }
  }, [response?.statusTotals]);

  // Calculate filter counts from statusTotals
  const filterCounts: Record<string, number> = useMemo(() => {
    const totals = response?.statusTotals || lastStatusTotals;

    if (!totals) {
      return {
        all: response?.total || 0,
        pending_data_enrichment: 0,
        ready_for_scheduling: 0,
        scheduled: 0,
        Scheduled: 0,
        rescheduled: 0,
        Rescheduled: 0,
        completed: 0,
        Completed: 0,
        conflicts: 0,
        conflictsTotal: 0,
        reassessment_due: 0,
        reassessmentDueTotal: 0
      };
    }

    // Calculate 'all' count from main status buckets only.
    // Conflicts are tracked separately and not included in the total.
    const statusValues = [
      totals.pending_data_enrichment || 0,
      totals.ready_for_scheduling || 0,
      totals.scheduled || 0,
      totals.rescheduled || 0,
      totals.completed || 0,
    ];

    const allFromTotals = statusValues.reduce((sum, val) => sum + val, 0);

    // Return counts with all available statuses
    return {
      all: allFromTotals || response?.total || 0,
      pending_data_enrichment: totals.pending_data_enrichment || 0,
      ready_for_scheduling: totals.ready_for_scheduling || 0,
      scheduled: totals.scheduled || 0,
      Scheduled: totals.scheduled || 0, // For Assessor (capitalized)
      rescheduled: totals.rescheduled || 0,
      Rescheduled: totals.rescheduled || 0, // For Assessor (capitalized)
      completed: totals.completed || 0,
      Completed: totals.completed || 0, // For Assessor (capitalized)
      conflicts: totals.conflicts || 0,
      conflictsTotal: totals.conflictsTotal || totals.conflicts || 0,
      reassessment_due: totals.reassessment_due || 0,
      reassessmentDueTotal: totals.reassessmentDueTotal || totals.reassessment_due || 0
    };
  }, [response, lastStatusTotals]);

  // Pagination calculations
  const totalItems = response?.total || 0;
  const totalPages = Math.ceil(totalItems / (pageSize || 1));

  // Refresh data callback
  const refreshData = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: [queryKey[0]] });
  }, [queryClient, queryKey]);

  return {
    // Data
    scheduleItems,
    isLoading,
    error: error as Error | null,
    isFetching,

    // Response
    response,

    // Status totals
    filterCounts,

    // Pagination
    totalItems,
    totalPages,

    // Actions
    refreshData
  };
}
